import React from 'react';
import {
  CALENDAR_PROVIDER_LABELS,
  CalendarEventDraft,
  CalendarProvider,
  addEventToCalendar,
  detectDefaultCalendarProvider,
} from '../../utils/addToCalendar';
import './AddToCalendarButton.css';

const MENU_CLEARANCE_PX = 140;

interface AddToCalendarButtonProps {
  /** `null` when the item has no resolvable date — the control then hides. */
  event: CalendarEventDraft | null;
  /** Icon-sized variant for dense rows like the day-plan timeline. */
  compact?: boolean;
  className?: string;
}

/**
 * Split control: pressing it uses the device's own calendar (Apple on
 * iPhone/iPad/Mac, Google elsewhere), and the chevron opens the other option
 * for travelers whose calendar isn't the platform one.
 */
const AddToCalendarButton: React.FC<AddToCalendarButtonProps> = ({
  event,
  compact = false,
  className,
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [opensUpward, setOpensUpward] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const defaultProvider = React.useMemo(() => detectDefaultCalendarProvider(), []);
  const providers = React.useMemo<CalendarProvider[]>(
    () => (defaultProvider === 'apple' ? ['apple', 'google'] : ['google', 'apple']),
    [defaultProvider],
  );

  React.useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handlePointerDown = (pointerEvent: MouseEvent) => {
      if (!containerRef.current?.contains(pointerEvent.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    const handleKeyDown = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === 'Escape') setIsMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  if (!event) return null;

  const defaultLabel = CALENDAR_PROVIDER_LABELS[defaultProvider];

  const handleAdd = (provider: CalendarProvider) => {
    setIsMenuOpen(false);
    addEventToCalendar(event, provider);
  };

  const handleToggleMenu = () => {
    // The day-plan timeline scrolls, so a menu opened on a low row would be
    // clipped — flip it above the button when there isn't room below.
    const bounds = containerRef.current?.getBoundingClientRect();
    if (bounds) setOpensUpward(window.innerHeight - bounds.bottom < MENU_CLEARANCE_PX);
    setIsMenuOpen((open) => !open);
  };

  return (
    <div
      ref={containerRef}
      className={`v2-add-to-calendar ${compact ? 'v2-add-to-calendar--compact' : ''} ${className || ''}`.trim()}
      // The day-plan row selects an activity on click; adding to a calendar
      // shouldn't also move the map.
      onClick={(clickEvent) => clickEvent.stopPropagation()}
    >
      <button
        type="button"
        className="v2-add-to-calendar__main"
        onClick={() => handleAdd(defaultProvider)}
        title={`Add to ${defaultLabel}`}
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <span>{compact ? 'Calendar' : `Add to ${defaultLabel}`}</span>
      </button>
      <button
        type="button"
        className="v2-add-to-calendar__toggle"
        aria-label="Choose a different calendar"
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        onClick={handleToggleMenu}
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isMenuOpen && (
        <div
          className={`v2-add-to-calendar__menu ${opensUpward ? 'v2-add-to-calendar__menu--up' : ''}`.trim()}
          role="menu"
          aria-label="Add to calendar"
        >
          {providers.map((provider) => (
            <button
              key={provider}
              type="button"
              role="menuitem"
              className="v2-add-to-calendar__menu-item"
              onClick={() => handleAdd(provider)}
            >
              {CALENDAR_PROVIDER_LABELS[provider]}
              {provider === defaultProvider && (
                <span className="v2-add-to-calendar__menu-hint">This device</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddToCalendarButton;
